import { ReactNode } from "react";
import {
  FaArrowLeft,
  FaBolt,
  FaHeart,
  FaInstagram,
  FaRegCommentDots,
} from "react-icons/fa";

export const INSTAGRAM_GRADIENT =
  "linear-gradient(45deg, #feda75 0%, #fa7e1e 25%, #d62976 50%, #962fbf 75%, #4f5bd5 100%)";

/** Иллюстрация: логотип Instagram с «наклейками» — молния (автоответ), сообщение, сердечко */
export const InstagramIllustration = () => (
  <div className="relative w-[220px] h-[200px] sm:w-[280px] sm:h-[250px]">
    <div
      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[140px] h-[140px] sm:w-[170px] sm:h-[170px] rounded-[40px] flex items-center justify-center text-white rotate-[-6deg]"
      style={{
        background: INSTAGRAM_GRADIENT,
        boxShadow:
          "0 0 0 8px rgba(255,255,255,0.12), 0 24px 60px rgba(214,41,118,0.45)",
      }}
    >
      <FaInstagram className="w-[84px] h-[84px] sm:w-[104px] sm:h-[104px]" />
    </div>
    <div className="absolute left-0 bottom-2 w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white flex items-center justify-center shadow-xl rotate-[-10deg]">
      <FaBolt className="text-[#d62976] w-7 h-7 sm:w-9 sm:h-9" />
    </div>
    <div className="absolute right-0 top-2 w-14 h-14 sm:w-[68px] sm:h-[68px] rounded-2xl bg-white flex items-center justify-center shadow-xl rotate-[8deg]">
      <FaRegCommentDots className="text-[#4f5bd5] w-6 h-6 sm:w-8 sm:h-8" />
    </div>
    <div className="absolute right-3 bottom-0 w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-[#e8f9b5] flex items-center justify-center shadow-xl rotate-[12deg]">
      <FaHeart className="text-[#962fbf] w-6 h-6 sm:w-7 sm:h-7" />
    </div>
  </div>
);

interface InstagramConnectLayoutProps {
  title: ReactNode;
  subtitle: ReactNode;
  illustration?: ReactNode;
  backLabel?: string;
  onBack?: () => void;
  children: ReactNode;
}

/**
 * Полноэкранный макет подключения Instagram (без меню и шапки админки):
 * слева тёмная панель с логотипом, иллюстрацией и «Назад», справа — содержимое.
 * Без flex-col у корня: глобальный .flex-col в стилях админки перебивает lg:flex-row.
 */
export const InstagramConnectLayout = ({
  title,
  subtitle,
  illustration = <InstagramIllustration />,
  backLabel = "Назад",
  onBack,
  children,
}: InstagramConnectLayoutProps) => (
  <div className="min-h-screen w-full flex flex-wrap content-start lg:content-stretch bg-white">
    <aside
      className="w-full lg:w-1/2 lg:min-h-screen flex flex-col px-6 py-6 sm:px-12 sm:py-10 lg:px-[10%] lg:py-14 text-white"
      style={{
        background:
          "radial-gradient(120% 90% at 30% 40%, #4a2366 0%, #351a4b 55%, #261236 100%)",
      }}
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center overflow-hidden">
          <img
            src="/media/images/ubee-logo.png"
            alt="Ubee AI"
            className="w-9 h-9 object-contain"
          />
        </div>
        <span className="text-2xl font-bold tracking-tight">Ubee AI</span>
      </div>

      <div className="flex-grow flex flex-col justify-center py-8 lg:py-0">
        <div className="mb-8 lg:mb-12">{illustration}</div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-3">
          {title}
        </h1>
        <p className="text-lg text-white/60">{subtitle}</p>
      </div>

      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="hidden lg:flex items-center gap-3 text-lg text-white/90 hover:text-white w-fit transition-colors"
        >
          <FaArrowLeft size={18} />
          {backLabel}
        </button>
      )}
    </aside>

    <main className="w-full lg:w-1/2 flex items-center px-6 py-10 sm:px-12 lg:px-[8%]">
      <div className="w-full max-w-[480px]">
        {children}

        {/* Обёртка без flex: глобальный .flex перебивает lg:hidden */}
        {onBack && (
          <div className="lg:hidden mt-8 text-center">
            <button
              type="button"
              onClick={onBack}
              className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <FaArrowLeft size={14} />
              {backLabel}
            </button>
          </div>
        )}
      </div>
    </main>
  </div>
);

export default InstagramConnectLayout;
